/**
 * SSOT Types — JB Interview Pro
 * Khớp 1-1 với schema DB tại migrations/01_interview_schema_v3.sql
 *
 * Convention: snake_case (đồng bộ DB, theo HANDOFF Phase 2 naming)
 */

// ============================================================================
// 1. user_cv (1-1 với users)
// ============================================================================

/** Một mục kinh nghiệm trong experiences[] (jsonb) */
export interface CvExperience {
  /** uuid client-side để FE quản lý array state. KHÔNG persist DB cột riêng. */
  id: string;
  company: string;
  position: string;
  /** YYYY-MM, vd "2022-06" */
  from_month: string;
  /** YYYY-MM, null nếu is_current=true */
  to_month: string | null;
  is_current: boolean;
  /** HTML đã sanitize ở write time (RichTextEditor onUpdate) */
  jd_description: string;
}

export interface UserCv {
  user_id: string;
  height_cm: number | null;
  weight_kg: number | null;
  school: string | null;
  major: string | null;
  strengths: string | null;
  weaknesses: string | null;
  skills_summary: string | null;
  experiences: CvExperience[];
  degree_type: import("./degree-types").DegreeTypeValue | null;
  postgrad_degree: import("./degree-types").PostgradDegreeValue | null;
  english_certs: string | null;
  computer_certs: string | null;
  created_at: string;
  updated_at: string;
}

export type UserCvInput = Omit<UserCv, "user_id" | "created_at" | "updated_at">;

// ============================================================================
// 2. interview_sessions
// ============================================================================

export interface ProfileSnapshot {
  full_name: string;
  gender: string | null;
  birthday: string;
  phone: string | null;
  height_cm: number | null;
  weight_kg: number | null;
  school: string | null;
  major: string | null;
  strengths: string | null;
  weaknesses: string | null;
  skills_summary: string | null;
  experiences: CvExperience[];
  degree_type: import("./degree-types").DegreeTypeValue | null;
  postgrad_degree: import("./degree-types").PostgradDegreeValue | null;
  english_certs: string | null;
  computer_certs: string | null;
}

export type SessionStatus =
  | "draft"
  | "analyzed"
  | "answers_ready"
  | "interviewing"
  | "completed";

/**
 * Một dòng red flag — đối chiếu yêu cầu vs thực tế CV.
 * ★ MOD: thêm humor + source (Phase humor)
 * ★ MOD: thêm ai_assessment (Phase cert check)
 */
export interface CvRedFlag {
  severity: "ok" | "warning" | "critical";
  requirement: string;
  actual: string;
  note: string;
  /** ★ NEW (Phase humor): câu hài hiển thị dưới note. Null = không có. */
  humor?: string | null;
  /** ★ NEW (Phase humor): nguồn gốc flag. 'db' = code resolve, 'ai' = AI sinh. */
  source?: "db" | "ai";
  /**
   * ★ NEW (Phase cert check): Nhận xét bổ sung hiển thị dưới note với heading
   * "Note hệ thống phân tích". Chỉ có giá trị với flag cert (english_cert/computer_cert)
   * khi AI compare cert user khai vs requirement DB.
   * KHÔNG dùng icon AI hoặc text "AI nhận xét" khi render.
   */
  ai_assessment?: string;
}

export interface CvFixSuggestion {
  field: string;
  current: string;
  suggested: string;
  reason: string;
}

export interface AnalysisData {
  match_score: number;
  age_match: number;
  degree_match?: number;          // ★ MỚI — % match bằng cấp
  major_match: number;
  /**
   * ★ NEW (Phase cert check): % match cert ngoại ngữ (0-100).
   * Backend tính từ status AI compare: pass→100, verify→50, fail→0.
   * undefined khi không có english_cert condition trong DB cho cặp org×position
   *   → UI render text fallback "Chưa có dữ liệu, AI sẽ tự động kiểm tra và cập nhật vào lần tới"
   */
  english_cert_match?: number;
  /**
   * ★ NEW (Phase cert check): % match cert tin học (0-100). Tương tự english_cert_match.
   */
  computer_cert_match?: number;
  missing_keywords: string[];

  cv_red_flags?: CvRedFlag[];
  cv_fix_suggestions?: CvFixSuggestion[];
  overall_assessment?: string;

  position_validity?: {
    status: "valid" | "questionable" | "invalid";
    reason: string;
    suggestions: string[];
  };

  verdict?: "pass" | "borderline" | "hard_fail";
  tone_detected?: "serious" | "casual" | "trolling";
  roasted_lines?: string[];
  alternative_suggestion?: string | null;

  /**
   * Banner hài nhẹ hiện ở Step 2 khi user nhập tổ chức/vị trí đặc biệt
   * (troll, lạ, edge case). Match từ table special_target_responses.
   * Không ảnh hưởng AI pipeline — chỉ là UI layer bổ sung.
   */
  special_banner?: {
    text: string;
    emoji: string | null;
  };

  /** @deprecated Phase 4.1 thay bằng cv_red_flags. */
  hard_fail_reasons?: string[];
  /** @deprecated Phase 4.1 thay bằng cv_fix_suggestions. */
  cv_suggestions?: string[];
}

export interface Step4Summary {
  total_score: number;
  radar: {
    knowledge: number;
    situational: number;
    defense: number;
    confidence: number;
  };
  strengths_overall: string[];
  weaknesses_overall: string[];
}

export interface InterviewSession {
  id: string;
  user_id: string;
  profile_snapshot: ProfileSnapshot;
  target_org_id: string | null;
  target_org_text: string | null;
  target_position_id: string | null;
  target_position_text: string | null;
  target_branch_text: string | null;
  analysis_data: AnalysisData | null;
  analysis_generated_at: string | null;
  analysis_snapshot_hash: string | null;
  cv_score_snapshot_hash: string | null;
  locked_at: string | null;
  step4_credits_remaining: number;
  step4_credits_purchased_total: number;
  step4_summary: Step4Summary | null;
  step4_completed_at: string | null;
  status: SessionStatus;
  created_at: string;
  updated_at: string;
}

// ============================================================================
// 3. interview_questions (Step 3)
// ============================================================================

export type AnswerStatus = "pending" | "generating" | "ready" | "error";

/**
 * ★ MOD: thêm intent + key_points + source_origin (Phase Hint+Key)
 */
export interface InterviewQuestion {
  id: string;
  session_id: string;
  order_idx: number;
  question_text: string;
  predicted_probability: number | null;
  answer_vi: string | null;
  answer_en: string | null;
  answer_status_vi: AnswerStatus | null;
  answer_status_en: AnswerStatus | null;
  source_question_id: string | null;
  user_edited: boolean;
  created_at: string;
  updated_at: string;

  /** ★ NEW (Phase Hint+Key): Ý đồ nhà tuyển dụng */
  intent: string | null;
  /** ★ NEW (Phase Hint+Key): Key trả lời. JSONB array ở DB. */
  key_points: string[] | null;
  /** ★ NEW (Phase Hint+Key): 'db' = pull từ bảng questions, 'ai' = AI sinh. */
  source_origin: "db" | "ai" | null;
}

export const ANSWER_STYLES = {
  professional: { label: "Chuyên nghiệp", emoji: "💼" },
  friendly: { label: "Thân thiện", emoji: "🤝" },
  creative: { label: "Sáng tạo", emoji: "✨" },
} as const;

export type AnswerStyle = keyof typeof ANSWER_STYLES;

// ============================================================================
// 4. interview_turns (Step 4)
// ============================================================================

export type QuestionType = "chuan" | "xoay" | "bat_ngo";

export interface AiFeedback {
  strengths: string[];
  weaknesses: string[];
  missing_keywords: string[];
  suggested_answer: string;
}

export interface InterviewTurn {
  id: string;
  session_id: string;
  turn_idx: number;
  question_type: QuestionType;
  question_text: string;
  source_question_id: string | null;
  follow_up_reason: string | null;
  user_answer_text: string | null;
  ai_score: number | null;
  ai_feedback: AiFeedback | null;
  answered_at: string | null;
  created_at: string;
}

// ============================================================================
// 5. Pricing constants (SSOT)
// ============================================================================

export const INTERVIEW_PRICING = {
  STEP3_UNLOCK_DIAMONDS: 100,
  STEP4_PACK_DIAMONDS: 100,
  STEP4_TURNS_PER_PACK: 20,
} as const;

export const INTERVIEW_QUESTIONS_PREVIEW = 10;
export const INTERVIEW_QUESTIONS_COUNT = 20;

// ============================================================================
// CV Score (Phase 2.6 — Step 1.5 chấm CV trước khi sang Step 2)
// ============================================================================

export type CheckStatus = "ok" | "warn" | "bad";

export interface CvCheckItem {
  label: string;
  value: string | null;
  status: CheckStatus;
  note: string;
}

export interface CvScoreData {
  checks: CvCheckItem[];
  /** @deprecated */ pass_score?: number;
  /** @deprecated */ pass_likelihood?: "low" | "mid" | "high";
  /** @deprecated */ summary?: string;
  /** @deprecated */ suggestions?: string[];
}

export const CV_LIMITS = {
  STRENGTHS_MAX: 500,
  WEAKNESSES_MAX: 500,
  SKILLS_SUMMARY_MAX: 1000,
  JD_DESCRIPTION_MAX: 2000,
  EXPERIENCES_DEFAULT: 5,
  HEIGHT_MIN: 120,
  HEIGHT_MAX: 200,
  WEIGHT_MIN: 40,
  WEIGHT_MAX: 150,
  SCHOOL_MAX: 150,
  MAJOR_MAX: 150,
  ENGLISH_CERTS_MAX: 500,
  COMPUTER_CERTS_MAX: 500,
} as const;

// ============================================================
// Admin Dashboard: Interview Demand Stats
// ============================================================

export interface InterviewDemandLog {
  session_id: string;
  created_at: string;
  user_name: string | null;
  org_name: string;
  org_source: 'fk' | 'text' | 'unknown';
  position_name: string;
  position_source: 'fk' | 'text' | 'unknown';
}

export interface InterviewDemandStats {
  rank: number;
  org_name: string;
  position_name: string;
  count: number;
  org_key: string;
  position_key: string;
}

export interface InterviewDemandPage<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}